import React, { useState } from "react";
import { ModelForm } from "./components/ModelForm";
import { useToast } from "@chakra-ui/react";
import { Mutation, MutationFn, Query } from "react-apollo";
import { RouteComponentProps } from "react-router";
import {
  getModelQuery,
  updateModelMutation,
  MODEL_RESPONSE_PAYLOAD,
  UpdateModelResult,
  UpdateModelVariables,
  GetModelVariables,
  GetModelResult,
  ModelInput,
} from "./queries";
import { RedirectIfNotAuthorized } from "./components/RedirectIfNotAuthorized";

const PAYLOAD = MODEL_RESPONSE_PAYLOAD.UPDATE;
const TOAST_DURATION = 5000;
const SUBMIT_BUTTON_ENABLED_DELAY = 500;

export const EditModel = (
  props: RouteComponentProps<{ id: string }>
): JSX.Element => {
  const id = props.match.params.id || "";

  const toast = useToast({ duration: TOAST_DURATION, isClosable: true });
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const enableFormSubmitButton = () => {
    setTimeout(() => {
      setIsFormSubmitting(false);
    }, SUBMIT_BUTTON_ENABLED_DELAY);
  };

  const disableFormSubmitButton = () => {
    setIsFormSubmitting(true);
  };

  const handleFormSubmit =
    (mutation: MutationFn<UpdateModelResult, UpdateModelVariables>) =>
    async (model: ModelInput) => {
      try {
        disableFormSubmitButton();
        const result = await mutation({
          variables: {
            id,
            model,
          },
        });

        if (!result) {
          toast({
            title: "Error",
            description: "An unknown error occurred.",
            status: "error",
          });
          return;
        }

        const message = result.data?.updateModel?.message;
        const payload = result.data?.updateModel?.payload;

        if (payload !== PAYLOAD.SUCCESS) {
          toast({
            title: "Error",
            description: message,
            status: "error",
          });
          enableFormSubmitButton();
          return;
        }

        toast({
          title: "Success",
          description: message,
          status: "success",
        });

        enableFormSubmitButton();
      } catch (err) {
        console.error(err);
        toast({
          title: "Error",
          description: "An unknown error occurred.",
          status: "error",
        });
        enableFormSubmitButton();
      }
    };

  return (
    <RedirectIfNotAuthorized>
      <Query<GetModelResult, GetModelVariables>
        query={getModelQuery}
        variables={{ id }}
      >
        {({ data, error, loading }) => {
          if (error) {
            return <div>Error fetching model.</div>;
          }

          if (loading) {
            return <div>Loading...</div>;
          }

          if (!data?.getModel?.id) {
            return <div>Not Found</div>;
          }

          return (
            <Mutation<UpdateModelResult, UpdateModelVariables>
              mutation={updateModelMutation}
            >
              {(mutation) => {
                return (
                  <ModelForm
                    title="Edit Model"
                    model={data.getModel}
                    onSubmit={handleFormSubmit(mutation)}
                    isFormSubmitting={isFormSubmitting}
                    buttonText="Update Model"
                    buttonLoadingText="Updating Model"
                  />
                );
              }}
            </Mutation>
          );
        }}
      </Query>
    </RedirectIfNotAuthorized>
  );
};
